import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import dashboardReducer from './slices/dashboardSlice';
import transactionReducer from './slices/transactionSlice';
import accountReducer from './slices/accountSlice';
import payeeReducer from './slices/payeeSlice';
import profileReducer from './slices/profileSlice';
import settingsReducer from './slices/settingsSlice';
import registrationReducer from './slices/registrationSlice';
import businessRegistrationReducer from './slices/businessRegistrationSlice';
import cardSecurityReducer from './slices/cardSecuritySlice';
import childWalletReducer from './slices/childWalletSlice';
import notificationReducer from './slices/notificationSlice';
import mt5ConnectionReducer from './slices/mt5ConnectionSlice';
import userSettingsReducer from './slices/userSettingsSlice';
import kytReducer from './slices/kytSlice';
import { sessionTimeoutMiddleware } from './middleware/sessionTimeoutMiddleware';
import { clearAuth } from './slices/authSlice';

// Utility function to detect if current route is admin
const isAdminRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/admin');
};

// Get the appropriate persistence key based on current context
const getAuthPersistenceKey = (): string => {
  return isAdminRoute() ? 'auth-admin' : 'auth-retail';
};

// Custom storage engine that handles context-aware keys
const createContextAwareStorage = () => {
  const baseStorage = storage;
  
  return {
    getItem: (key: string) => {
      // For auth, use context-aware key
      if (key === 'auth') {
        const contextKey = getAuthPersistenceKey();
        console.log(`📖 Reading auth data from: ${contextKey}`);
        const data = baseStorage.getItem(contextKey);
        console.log(`📖 Auth data from ${contextKey}:`, data !== null ? 'present' : 'absent');
        return data;
      }
      return baseStorage.getItem(key);
    },
    setItem: (key: string, value: string) => {
      // For auth, use context-aware key
      if (key === 'auth') {
        const contextKey = getAuthPersistenceKey();
        console.log(`💾 Saving auth data to: ${contextKey}`);
        return baseStorage.setItem(contextKey, value);
      }
      return baseStorage.setItem(key, value);
    },
    removeItem: (key: string) => {
      // For auth, use context-aware key
      if (key === 'auth') {
        const contextKey = getAuthPersistenceKey();
        console.log(`🗑️ Removing auth data from: ${contextKey}`);
        return baseStorage.removeItem(contextKey);
      }
      return baseStorage.removeItem(key);
    }
  };
};

// Create separate persistence configs for retail and admin
// NOTE: adminUser is intentionally excluded from whitelist so permissions are
// always hydrated fresh from the login response, never from stale cached data.
const authPersistConfig = {
  key: 'auth',
  version: 2, // bump this whenever the adminUser shape changes to force cache eviction
  storage: createContextAwareStorage(),
  whitelist: ['user', 'token', 'refreshToken', 'tokenExpiry', 'lastActivity', 'type', 'isAuthenticated'],
  // adminUser is NOT persisted — it is always populated from loginAdmin.fulfilled
  migrate: (state: any) => {
    // Wipe any legacy persisted adminUser so stale permissions can't block access
    if (state && state.adminUser) {
      return Promise.resolve({ ...state, adminUser: null });
    }
    return Promise.resolve(state);
  },
};

// Persist configuration for child wallet slice to save QR codes
const childWalletPersistConfig = {
  key: 'childWallet',
  storage,
  whitelist: ['activePaymentRequest', 'paymentRequestHistory', 'selectedChild'], // Persist QR code data and selected child
};

// Persist configuration for KYT slice to save analysis cache and search history
const kytPersistConfig = {
  key: 'kyt',
  storage,
  whitelist: ['analysisCache', 'traceCache', 'validationCache', 'searchHistory', 'currentAddress', 'currentBlockchain'],
};

// Persist registration drafts so Stripe redirects / full page reloads don't wipe in-progress forms
const registrationPersistConfig = {
  key: 'registration',
  storage,
  whitelist: ['personal', 'business', 'sessionTimeout'],
};

// Combine reducers with context-aware auth persistence
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  dashboard: dashboardReducer,
  transactions: transactionReducer,
  accounts: accountReducer,
  payees: payeeReducer,
  profile: profileReducer,
  settings: settingsReducer,
  userSettings: userSettingsReducer,
  registration: persistReducer(registrationPersistConfig, registrationReducer),
  businessRegistration: businessRegistrationReducer,
  cardSecurity: cardSecurityReducer,
  childWallet: persistReducer(childWalletPersistConfig, childWalletReducer),
  notifications: notificationReducer,
  mt5Connection: mt5ConnectionReducer, // Add the MT5 connection reducer
  kyt: persistReducer(kytPersistConfig, kytReducer), // Add the KYT reducer with persistence
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // Ignore large objects that might cause performance issues
        ignoredPaths: ['kyt.traceCache', 'kyt.analysisCache', 'kyt.validationCache'],
      },
      // Increase the warning threshold for immutable state invariant middleware
      immutableCheck: {
        warnAfter: 64, // Increase from default 32ms to 64ms
      },
    }).concat(sessionTimeoutMiddleware),
});

export const persistor = persistStore(store);

// Export utility functions for external use
export const getCurrentContext = () => getAuthPersistenceKey();
export const isInAdminContext = () => isAdminRoute();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch; 